import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Banknote, Truck } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/lib/seo";
import { api, formatApiError } from "@/lib/api";
import { useContent } from "@/lib/content";
import { useCustomer, startGoogleLogin } from "@/lib/customer";

export default function Merci() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const { t } = useContent();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { customer, setCustomer } = useCustomer();
  const [acc, setAcc] = useState({ email: state?.email || "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(false);

  const createAccount = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/customer/register", { name: state?.name || "Cliente", email: acc.email, password: acc.password, phone: state?.phone || "", order_ref: ref || "" });
      setCustomer(data);
      setCreated(true);
      toast.success("Compte créé — votre commande y est rattachée");
      navigate("/compte");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="merci-page" className="min-h-[85vh] flex items-center justify-center bg-white pt-28 pb-20 px-6">
      <SEO title="Merci pour votre commande" description="Votre commande est bien reçue. Nous vous appelons très vite pour la livraison à Dakar — paiement à la réception." />
      <motion.div
        className="max-w-lg text-center"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.span
          data-testid="merci-check"
          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-mint text-teal-dark"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", damping: 12, delay: 0.2 }}
        >
          <Check size={38} strokeWidth={3} />
        </motion.span>
        <h1 className="mt-8 font-serif text-4xl md:text-5xl text-teal-dark">{t("merci.title")}</h1>
        {ref && (
          <p className="mt-4 text-ink">
            Votre commande <span data-testid="merci-ref" className="font-mono font-semibold text-teal bg-mint/20 rounded-lg px-3 py-1">{ref}</span> est bien enregistrée.
          </p>
        )}
        <div className="mt-8 space-y-3 text-left bg-cream rounded-3xl border border-line p-6">
          <p className="flex gap-3 text-sm text-ink/80"><Truck size={18} className="text-teal shrink-0 mt-0.5" />{t("merci.line1")}</p>
          <p className="flex gap-3 text-sm text-ink/80"><Banknote size={18} className="text-teal shrink-0 mt-0.5" />{t("merci.line2")}</p>
        </div>
        <Link data-testid="merci-back-boutique" to="/boutique" className="mt-9 inline-block rounded-full bg-teal px-10 py-4 font-medium text-cream hover:bg-teal-dark transition-colors active:scale-[0.98]">
          {t("merci.cta")}
        </Link>
        {customer === null && !created && (
          <form onSubmit={createAccount} data-testid="merci-create-account" className="mt-10 text-left rounded-3xl border border-line bg-white p-6">
            <p className="font-serif text-xl text-teal-dark">Suivre ma commande en un clic</p>
            <p className="mt-1 text-sm text-ink/70">Créez votre compte : cette commande y sera rattachée et vous commanderez plus vite la prochaine fois.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required type="email" data-testid="merci-email-input" value={acc.email} onChange={(e) => setAcc({ ...acc, email: e.target.value })} placeholder="Votre email" className="rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
              <input required type="password" autoComplete="new-password" data-testid="merci-password-input" value={acc.password} onChange={(e) => setAcc({ ...acc, password: e.target.value })} placeholder="Mot de passe (8 car. min.)" className="rounded-2xl border border-line bg-cream-light px-4 py-3 text-sm outline-none focus:border-teal" />
            </div>
            {err && <p data-testid="merci-account-error" className="mt-3 text-xs text-[#b04a4a]">{err}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <button type="submit" data-testid="merci-create-account-btn" disabled={busy} className="rounded-full bg-teal-dark px-6 py-2.5 text-sm font-medium text-cream disabled:opacity-60">{busy ? "Création…" : "Créer mon compte"}</button>
              <button type="button" data-testid="merci-google-btn" onClick={startGoogleLogin} className="text-sm text-teal link-underline">ou continuer avec Google</button>
            </div>
          </form>
        )}
        {customer && (
          <p className="mt-8 text-sm text-ink/70">
            Retrouvez cette commande dans <Link to="/compte" data-testid="merci-account-link" className="text-teal font-medium link-underline">Mon compte</Link>.
          </p>
        )}
      </motion.div>
    </div>
  );
}
